// Traduce los rechazos del OTP del silo a algo que el vendedor pueda accionar frente al cliente:
// pedir otro código, volver a teclear o esperar.
import { RequestTimeoutError } from '@/api/client';
import { ApiError } from '@/api/errors';

/** Segundos que faltan para poder reenviar. `undefined` si el silo no informó o no fue cooldown. */
export function resendCooldownSeconds(error: unknown): number | undefined {
    if (!(error instanceof ApiError) || error.code !== 'OTP_RESEND_COOLDOWN') return undefined;
    const left = (error.data as { secondsLeft?: unknown } | undefined)?.secondsLeft;
    return typeof left === 'number' ? left : undefined;
}

const SALE_CLOSED = 'Esta venta ya no está activa. Vuelve al portal e inicia una nueva.';
const SALE_GONE = 'Esta venta ya no existe. Vuelve al portal e inicia una nueva.';

/** Mensaje cuando falla el ENVÍO del código. */
export function sendOtpMessage(error: unknown): string {
    const seconds = resendCooldownSeconds(error);
    if (seconds !== undefined) {
        return `No salió un código nuevo: el anterior se envió hace menos de un minuto. Espera ${String(seconds)} s y usa "Reenviar".`;
    }
    if (error instanceof ApiError) {
        if (error.code === 'OTP_RESEND_COOLDOWN') {
            return 'No salió un código nuevo: el anterior se envió hace menos de un minuto. Espera a que se habilite "Reenviar".';
        }
        if (error.code === 'INVALID_SESSION_STATE') return SALE_CLOSED;
        if (error.code === 'SESSION_NOT_FOUND') return SALE_GONE;
    }
    return 'No se pudo enviar el código. Usa "Reenviar" en unos segundos.';
}

const VERIFY_MESSAGE_BY_CODE: Record<string, string> = {
    // Vencido y ya usado piden lo MISMO (otro código) pero se leen distinto: uno le dice al vendedor
    // que el cliente se tardó, el otro que está tecleando el de una venta anterior.
    OTP_EXPIRED: 'El código venció. Pide uno nuevo con "Reenviar".',
    OTP_ALREADY_USED: 'Ese código ya se usó. Pide uno nuevo con "Reenviar".',
    OTP_INVALID: 'Código incorrecto. Confirma los 4 dígitos con el cliente.',
    OTP_MAX_TRIES: 'Se agotaron los intentos con ese código. Pide uno nuevo con "Reenviar".',
    // El que falló fue messages-business, no el cliente: decirle "incorrecto" a quien tecleó bien es
    // el peor desenlace posible de esta pantalla.
    EXTERNAL_INTEGRATION_ERROR:
        'No pudimos validar el código en este momento. Espera unos segundos e intenta de nuevo.',
    INVALID_SESSION_STATE: SALE_CLOSED,
    SESSION_NOT_FOUND: SALE_GONE,
    PERMISSION_DENIED: 'No tienes permiso para operar esta venta.',
};

/** Mensaje cuando falla la VERIFICACIÓN del código tecleado. */
export function verifyOtpMessage(error: unknown): string {
    if (error instanceof RequestTimeoutError) {
        return 'La verificación tardó demasiado. Intenta de nuevo antes de pedir otro código.';
    }
    if (!(error instanceof ApiError)) {
        return 'No se pudo verificar el código. Intenta de nuevo.';
    }
    return (
        VERIFY_MESSAGE_BY_CODE[error.code] ??
        'No se pudo verificar el código. Intenta de nuevo o pide uno nuevo con "Reenviar".'
    );
}
